import React, {useCallback, useEffect, useMemo} from 'react';
import { useGames, useHome, useJobs } from "../../api/api";
import { useMediaQuery } from "react-responsive";
import { useTranslation } from "react-i18next";
import { TABLET } from "../../constants/constants";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import { Helmet } from "react-helmet";
import useImagePreloader from "../../utils/useImagePreloader";
import LazyLoad from 'react-lazyload';
import Error from "../404/Error";
import Loader from "../../components/Loader";
import Container from '../../components/Container';
import Button from '../../components/Button';
import AnimatedCard from '../../components/Card/AnimatedCard';
import Parallax from "../../components/Parallax";
import Separator from "../../components/Separator";
import GameBlock from "../../components/GameBlock";
import JobCard from "../../components/JobCard";
import AnimateOnce from "../../components/AnimateOnce";
import Dot from "../../components/Dot";
import fire from './assets/video/fire.mp4';
import caveBg from './assets/bg/bg_cave.webp';
import ship from './assets/ship/ship.png';
import leftCave from './assets/caves/cave_left.webp';
import rightCave from './assets/caves/cave_right.webp';
import pirate1 from './assets/pirates/pirates1_5.png';
import pirate2 from './assets/pirates/pirates2_4.png';
import pirate3 from './assets/pirates/pirates3.png';
import piratesMobile from './assets/pirates/pirates_mobile@2x.png';
import octopus from './assets/cards/octopus.png';
import bark from './assets/cards/bark.png';
import bottle from './assets/cards/bottle.png';
import box from './assets/cards/box.png';
import aboutBG from './assets/bg/bg_about.webp';
import weCreateBG from './assets/bg/bg_wecreate.webp';
import missionBG from './assets/bg/bg_mission.webp';
import gamesBG from './assets/bg/bg_games.webp';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import styles from './home.module.scss';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Page from "../../components/Page";

const preloadSrcList = [
  caveBg,
  ship,
  leftCave,
  rightCave,
  pirate1,
  pirate2,
  pirate3,
  piratesMobile,
  aboutBG,
  weCreateBG,
  missionBG,
  gamesBG,
  octopus,
  bark,
  bottle,
  box,
];

const gameSliderSettings = {
  ssr: false,
  removeArrowOnDeviceType: ["desktop", "tablet", "mobile"],
  draggable: true,
  showDots: true,
  dotListClass: styles.dots,
  customDot: <Dot />,
  itemClass: styles.gameSlide,
  responsive: {
    desktop: {
      breakpoint: { max: 9999, min: 1152 },
      items: 1,
      arrows: false,
      showDots: false,
    },
    tablet: {
      breakpoint: { max: 1152, min: 464 },
      showDots: true,
      items: 1
    },
    mobile: {
      breakpoint: { max: 480, min: 0 },
      items: 1
    }
  }
}

const jobSliderSettings = {
  ssr: false,
  removeArrowOnDeviceType: ["medium", "mobile"],
  draggable: true,
  showDots: true,
  dotListClass: styles.jobDots,
  customDot: <Dot />,
  infinite: true,
  responsive: {
    medium: {
      breakpoint: { max: 854, min: 464 },
      showDots: true,
      items: 1,
      infinite: true,
    },
    mobile: {
      breakpoint: { max: 480, min: 0 },
      items: 1,
      infinite: false,
    }
  }
};

const Home = () => {
  const isTablet = useMediaQuery({ maxDeviceWidth: TABLET });
  const [t, i18n] = useTranslation("global");
  const lang = useMemo(() => i18n.language.slice(0, 2), [i18n.language]);
  const { imagesPreloaded } = useImagePreloader(preloadSrcList);
  const { homeData, isHomeLoading, homeError } = useHome(lang);
  const { jobsData, isJobsLoading, jobsError } = useJobs(lang);
  const { gamesData, isGamesLoading, gamesError } = useGames(lang);

  const handleScroll = useCallback(() => {
    document.body.style.cssText += `--scrollTop: ${window.scrollY}`;
    document.body.style.cssText += `--scrollTopPX: ${window.scrollY}px`;
  }, []);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    document.body.style.cssText += `--scrollTop: ${window.scrollY ?? 0}`;
    document.body.style.cssText += `--scrollTopPX: ${window.scrollY ?? 0}px`;

    window.addEventListener('wheel', handleScroll);
    window.addEventListener('keydown', handleScroll);
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('touchmove', handleScroll);

    return () => {
      window.removeEventListener('wheel', handleScroll);
      window.removeEventListener('keydown', handleScroll);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('touchmove', handleScroll);
    };
  }, [lang, handleScroll]);

  if (isHomeLoading || isJobsLoading || isGamesLoading || !imagesPreloaded) {
    return <Loader />
  }

  if (
    homeError ||
    homeData?.error ||
    jobsError ||
    jobsData?.error ||
    gamesError ||
    gamesData?.error
  ) {
    return <Error />
  }

  const home = homeData.data || null;
  const jobs = jobsData.data.slice(0, 3) || null;
  const games = gamesData.data || null;

  return (
    <Page>
      <Helmet>
        <title>RedBark Studio</title>
      </Helmet>

        <>
          <div className={styles.topSection}>
            <div className={`${styles.mainScreen}`}>

              <div className={`${styles.caveBg}`}>
                <img src={caveBg} alt='caveBg' />
              </div>

              <LazyLoad height={'100%'}>
                <video className={styles.mainVideo} autoPlay loop muted>
                  <source src={fire} type={'video/mp4'}/>
                </video>
              </LazyLoad>

              <div className={`${styles.leftCave} ${styles.withoutAnimation}`}>
                <img src={leftCave} alt='left cave'/>
              </div>
              <div className={`${styles.rightCave} ${styles.withoutAnimation}`}>
                <img src={rightCave} alt='right cave' />
              </div>

              { !isTablet &&
                <div className={`${styles.pirates}`}>
                  <div className={`${styles.pirate1}`}>
                    <img src={pirate1} alt="pirates" />
                  </div>
                  <div className={`${styles.pirate2}`}>
                    <img src={pirate2} alt="pirates" />
                  </div>
                  <div className={`${styles.pirate3}`}>
                    <img src={pirate3} alt="pirates" />
                  </div>
                </div>
              }

              { isTablet &&
                <div className={styles.pirates}>
                  <div className={`${styles.piratesMobile}`}>
                    <img src={piratesMobile} alt="pirates" />
                  </div>
                </div>
              }

              <div className={styles.mainCentral}>
                <div className={`${styles.mainShip} ${styles.withoutAnimation}`}>
                  <img src={ship} alt="ship" />
                </div>
                { home?.title &&
                  <div
                    className={`
                      ${styles.mainTitle} 
                      ${styles.withoutAnimation}
                    `}
                  >
                    <h1>{home.title}</h1>
                  </div>
                }
                { home?.game_button === true &&
                  <div className={`${styles.mainButton}`}>
                    <Button type={'link'} text={t("buttons.games")} to={'/games'}/>
                  </div>
                }
              </div>
            </div>
            <div className={`${styles.aboutSection} ${styles.withoutAnimation}`}>
              <img className={styles.aboutBg} src={aboutBG} alt='aboutBG' />
              <Container className={styles.aboutPage}>
                <div className="row">
                  <div className="col-lg-6 col-md-6 col-sm-12 flex-col-center-v">
                    <div className={styles.left}>
                      <Parallax speed={!isTablet ? -0.4 : 0.5}>
                        <div className={styles.aboutDesc}>
                          <h2>{home.about.title}</h2>
                          <div className="text-large">
                            <p>{home.about.description}</p>
                          </div>
                        </div>
                      </Parallax>
                      <Parallax speed={!isTablet ? -0.42 : 0.58}>
                        <Button
                          text={t("buttons.learn")}
                          type={'link'}
                          to={'/about'}
                          className={styles.aboutButton}
                        />
                      </Parallax>
                    </div>
                  </div>
                  <div className="col-lg-6 col-md-6 col-sm-12 flex-col-center-v">
                    <AnimatedCard
                      className={styles.aboutCard}
                      speedCard={.6}
                      speedImg={.4}
                      img={octopus}
                      text={home.about.card_title}
                    />
                  </div>
                </div>
              </Container>
              <Separator />
            </div>
          </div>

          <LazyLoad height={1600} offset={1000}>
            <div className={`section ${styles.section}`}>
            <img className={styles.bgImage} src={weCreateBG} alt='weCreateBG'/>
            <Container className={`${styles.page} ${styles.weCreate}`}>
              <div className="row">
                <div className="col-lg-6 col-md-6 col-sm-12 flex-col-center-v">
                  <AnimatedCard
                    className={styles.weCreateCard}
                    speedCard={!isTablet ? 0.2 : 0.8}
                    speedImg={0.1}
                    img={bark}
                    rotate={'-5deg'}
                    text={home.we_create.card_title}
                    translateX={15}
                  />
                </div>
                <div className="col-lg-6 col-md-6 col-sm-12 flex-col-center-v">
                  <Parallax speed={!isTablet ? -0.5 : 0.58}>
                    <div className={`${styles.weCreateDesc} ${styles.right}`}>
                      <h3 className={styles.weCreateTitle}>{home.we_create.title}</h3>
                      <div className="text-large">
                        <BlocksRenderer content={home.we_create.description} />
                      </div>
                    </div>
                  </Parallax>
                </div>
              </div>
            </Container>
            <Separator />
          </div>
          </LazyLoad>

          <LazyLoad height={1600} offset={1600}>
            <div className={`section ${styles.section}`}>
            <img className={styles.bgImage} src={missionBG} alt='missionBG' />
            <Container className={`${styles.page} ${styles.mission}`}>
              {!isTablet &&
                <div className="row">
                  <div className={`text-center ${styles.missionTop}`}>
                    <h2 className='text-pink'>{home.mission.title}</h2>
                    <div className="text-large">
                      <p>{home.mission.description}</p>
                    </div>
                  </div>
                </div>
              }
              <div className="row">
                <div className="col-lg-4 col-md-4 flex-col-center-v">
                  <div className={`${styles.left} ${styles.missionDesc}`}>
                    <Parallax speed={!isTablet ? -0.5 : 0.5}>
                      <h3 className={`text-pink ${styles.missionSubTitle}`}>{home.mission.sub_title}</h3>
                      <div className="text-large">
                        <p>{home.mission.sub_description}</p>
                      </div>
                    </Parallax>
                    <Parallax speed={!isTablet ? -0.4 : 0.5}>
                      <Button
                        type={'link'}
                        to={'/mission'}
                        text={t("buttons.learn")}
                        className={styles.missionBtn}
                      />
                    </Parallax>
                  </div>
                </div>
                <div className="col-lg-4 col-md-4 flex-col-center-v">
                  <Parallax speed={!isTablet ? -0.01 : 0.01} start={'top bottom'} rotate={true} rotateTo={'-15deg'}>
                    <div className={styles.missionBox}>
                      <img src={box} alt='box' />
                    </div>
                  </Parallax>
                </div>
                {isTablet &&
                  <Parallax speed={0.5}>
                    <div className="col-md-4">
                      <div className={`text-center ${styles.missionTop}`}>
                        <h2 className='text-pink'>{home.mission.title}</h2>
                        <div className="text-large">
                          <p>{home.mission.description}</p>
                        </div>
                      </div>
                    </div>
                  </Parallax>
                }
                <div className="col-lg-4 col-md-4 flex-col-center-v">
                  <div className={styles.right}>
                    <AnimatedCard
                      className={styles.missionCard}
                      speedCard={!isTablet ? 0.2 : 0.5}
                      speedImg={!isTablet ? .4 : 0.5}
                      img={bottle}
                      rotate={'5deg'}
                      text={home.mission.card_title}
                    />
                  </div>
                </div>
              </div>
            </Container>
            <Separator />
          </div>
          </LazyLoad>


          <LazyLoad height={3000} offset={1600}>
            <div className={`section ${styles.section}`}>
            <img className={styles.bgImage} src={gamesBG} alt='gamesBG' />
            { games && games.length > 0 &&
              <Container className={`${styles.page}`}>
                <div className={styles.left}>
                  <Parallax speed={!isTablet ? -.5 : -0.8}>
                    <div className="row mb30">
                      <div className={`col-lg-12 ${styles.gamesDesc}`}>
                        <h2 className='text-pink'>{home.games.title}</h2>
                        <div className="text-large">
                          <p>{home.games.description}</p>
                        </div>
                      </div>
                    </div>
                  </Parallax>
                  {
                    games.length === 1 &&
                    <>
                      {
                        games.map((game) => (
                          <GameBlock
                            key={game.id}
                            className={styles.gameBlock}
                            data={game}
                          />
                        ))
                      }
                    </>
                  }
                  { games.length > 1 &&
                    <Carousel {...gameSliderSettings} className={styles.gameSlider}>
                      {
                        games.map((game) => (
                          <GameBlock
                            key={game.id}
                            className={styles.gameBlock}
                            data={game}
                          />
                        ))
                      }
                    </Carousel>
                  }
                </div>
              </Container>
            }

            { jobs && jobs.length > 0 &&
              <Container className={`${styles.page} ${styles.crew}`}>
                <Parallax speed={!isTablet ? .3 : -.7}>
                  <div className="row">
                    <div className={`col-lg-6 col-lg-offset-3 text-center ${styles.crewDesc}`}>
                      <h2 className='text-pink'>{home.recruitment.title}</h2>
                      <div className="text-large">
                        <p>{home.recruitment.description}</p>
                      </div>
                    </div>
                  </div>
                </Parallax>
                <div className="row flex-col-center">
                  {!isTablet ?
                    <Parallax speed={1}>
                      <AnimateOnce
                        styleFrom={{
                          transform: "translateX(100%)",
                          opacity: 0,
                        }}
                        styleTo={{
                          x: 0,
                          opacity: 1,
                          duration: 1
                        }}
                      >
                      <div className={styles.jobs}>
                        {
                          jobs.map((job, index) => (
                            <JobCard
                              key={index}
                              className={styles.jobCard}
                              data={job}
                            />
                          ))
                        }
                      </div>
                      </AnimateOnce>
                    </Parallax> :

                    <Carousel {...jobSliderSettings} className={styles.jobsSlider}>
                      {
                        jobs.map((job, index) => (
                          <JobCard
                            key={index}
                            className={styles.jobCard}
                            data={job}
                          />
                        ))
                      }
                    </Carousel>
                  }
                </div>
              </Container>
            }
            <Separator />
          </div>
          </LazyLoad>
        </>
    </Page>

  );
};

export default Home;